/**
 * REK-2186: optional `feature_flags` row `auth.social_providers` — boolean
 * `enabled` kill-switch + optional JSON in `description` for order/disabled.
 * There is no JSON column on `feature_flags` (boolean + text only).
 */

export const AUTH_SOCIAL_PROVIDERS_FLAG_KEY = 'auth.social_providers';

/** Category used when querying `feature_flags` for the social kill-switch. */
export const AUTH_SOCIAL_PROVIDERS_FLAG_CATEGORY = 'auth';

export interface SocialProvidersFlagConfig {
  /** Preferred strategy order (`oauth_google`, …). Unknown entries ignored. */
  order: string[];
  /** Strategies to hide even when Clerk marks them authenticatable. */
  disabled: string[];
}

const EMPTY_CONFIG: SocialProvidersFlagConfig = { order: [], disabled: [] };

/**
 * Parse optional `{ "order": [...], "disabled": [...] }` from the flag's
 * `description` text. Invalid / non-JSON descriptions → empty config (Clerk
 * defaults still apply when the flag is enabled / absent).
 */
export function parseSocialProvidersFlagDescription(
  description: string | null | undefined,
): SocialProvidersFlagConfig {
  if (!description || !description.trim()) return { ...EMPTY_CONFIG };
  try {
    const parsed = JSON.parse(description) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ...EMPTY_CONFIG };
    }
    const record = parsed as Record<string, unknown>;
    const order = Array.isArray(record.order)
      ? record.order.filter((v): v is string => typeof v === 'string')
      : [];
    const disabled = Array.isArray(record.disabled)
      ? record.disabled.filter((v): v is string => typeof v === 'string')
      : [];
    return { order, disabled };
  } catch {
    return { ...EMPTY_CONFIG };
  }
}

/**
 * Apply kill-switch + order/disabled to Clerk's authenticatable social set.
 *
 * - `flagEnabled === false` → empty (hide all social buttons).
 * - `flagEnabled === null` (row absent) → Clerk set, google-first when present.
 * - `flagEnabled === true` → Clerk set minus `disabled`, ordered by `order`.
 */
export function applySocialProvidersFlag<T extends { strategy: string }>(
  strategies: T[],
  options: {
    flagEnabled: boolean | null;
    config: SocialProvidersFlagConfig;
  },
): T[] {
  if (options.flagEnabled === false) return [];

  const disabled = new Set(
    options.config.disabled.map((s) => s.trim().toLowerCase()).filter(Boolean),
  );
  const filtered = strategies.filter(
    (s) => !disabled.has(s.strategy.trim().toLowerCase()),
  );

  const order = options.config.order
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  if (order.length === 0) {
    const googleIdx = filtered.findIndex(
      (s) => s.strategy.toLowerCase() === 'oauth_google',
    );
    if (googleIdx <= 0) return filtered;
    const next = [...filtered];
    const [google] = next.splice(googleIdx, 1);
    return [google, ...next];
  }

  const byStrategy = new Map(
    filtered.map((s) => [s.strategy.toLowerCase(), s] as const),
  );
  const ordered: T[] = [];
  for (const key of order) {
    const hit = byStrategy.get(key);
    if (hit) {
      ordered.push(hit);
      byStrategy.delete(key);
    }
  }
  for (const remaining of byStrategy.values()) {
    ordered.push(remaining);
  }
  return ordered;
}
